'use client'

import { useEffect, useState } from 'react'
import BEMHelper from '../../helpers/bem'
import Button from '../Button'
import Link from 'next/link'
import styles from './Styles.module.scss'

const bem = BEMHelper(styles)
const CONSENT_KEY = 'tracking-amplitude-cookie-consent'
const AMPLITUDE_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY

const deleteAmplitudeCookies = () => {
  if (typeof window === 'undefined' || !AMPLITUDE_API_KEY) return

  const cookieName = `AMP_${AMPLITUDE_API_KEY.substring(0, 10)}`
  const cookieNameMktg = `AMP_MKTG_${AMPLITUDE_API_KEY.substring(0, 10)}`
  const cookies = document.cookie.split(';')

  cookies.forEach((cookie) => {
    const [name] = cookie.trim().split('=')

    if (name === cookieName || name === cookieNameMktg) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.faktisk.no; SameSite=Lax`
      console.info(`🗑️ Deleted Amplitude cookie: ${name}`)
    }
  })
}

export const getCookieConsent = (): 'accepted' | 'declined' | null => {
  if (typeof window === 'undefined') return null
  const consent = localStorage.getItem(CONSENT_KEY)
  return consent as 'accepted' | 'declined' | null
}

export const deleteCookieConsent = (): void => {
  if (typeof window === 'undefined') return
  localStorage.removeItem(CONSENT_KEY)
}

export function ResetConsentButton({ className }: { className?: string }) {
  const [hasConsent, setHasConsent] = useState(false)

  useEffect(() => {
    setHasConsent(getCookieConsent() !== null)
  }, [])

  const handleResetConsent = () => {
    deleteCookieConsent()
    setHasConsent(false)

    // Dispatch event to notify that consent was removed
    window.dispatchEvent(
      new CustomEvent('cookieConsentChanged', {
        detail: { consent: null },
      }),
    )

    // Reload the page to show the cookie popup again and reset Amplitude
    window.location.reload()
  }

  if (!hasConsent) {
    return null
  }

  return (
    <button type="button" onClick={handleResetConsent} className={className}>
      Endre samtykke
    </button>
  )
}

export default function Cookie() {
  const [showPopup, setShowPopup] = useState(false)

  useEffect(() => {
    // Check if consent is already stored
    const consent = getCookieConsent()
    if (consent === null) {
      setShowPopup(true)
    }
  }, [])

  const handleConsent = (accepted: boolean) => {
    localStorage.setItem(CONSENT_KEY, accepted ? 'accepted' : 'declined')
    setShowPopup(false)

    // If user declines, delete any existing Amplitude cookies
    if (!accepted) {
      deleteAmplitudeCookies()
    }

    // Dispatch custom event to notify other components
    window.dispatchEvent(
      new CustomEvent('cookieConsentChanged', {
        detail: { consent: accepted ? 'accepted' : 'declined' },
      }),
    )
  }

  if (!showPopup) {
    return null
  }

  return (
    <aside {...bem('')}>
      <p {...bem('text')}>
        Vi bruker cookies for å få statistikk om besøket ditt til intern bruk. Se{' '}
        <Link href="/artikkel/personvern">personvern</Link> for mer.
      </p>

      <Button primary full small {...bem('button')} onClick={() => handleConsent(true)}>
        Greit
      </Button>
      <Button primary full small {...bem('button', 'muted')} onClick={() => handleConsent(false)}>
        Nei takk
      </Button>
    </aside>
  )
}
